import { Avatar, Dropdown } from "antd"
import { AGENCY_SETTINGS_PATH, EDIT_STAFF_PATH, LOGIN_PATH } from "constants/app-paths"
import { useLogoutMutation } from "features/auth/api"
import ScanQrCode from "features/layout/scanQrCode/scanQrCode"
import { useLazyGetGAQrCodeLoginQuery, useLazyGetQrCodeLoginQuery } from "features/shared/api"
import { useRef } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { Link, useNavigate } from "react-router-dom"
import { logout } from "store/appSlice"
import parentApi from "store/parentApi"
import getPrivilege from "utils/privileges/privileges"
import theme from "utils/themeVars"
import { Button, Divider, Icon, Spinner, Text, Title } from "../../common"
import styles from "./userMenu.module.less"

const UserMenu = ({ onClickLink }) => {
  const { t, i18n } = useTranslation()
  const user = useSelector(state => state.app.user?.info)
  const userPrivileges = useSelector(state => state.app.privileges)
  const navigate = useNavigate()
  const qrCodeModalRef = useRef()
  const gAQrCodeModalRef = useRef()
  const dispatch = useDispatch()
  const [logoutUser, { isLoading }] = useLogoutMutation()

  const handleLogout = () => {
    logoutUser({})
    dispatch(logout())
    dispatch(parentApi.util.resetApiState())
    navigate(LOGIN_PATH)
  }

  const items = [
    {
      label: (
        <Link to={AGENCY_SETTINGS_PATH} onClick={() => onClickLink()}>
          {t("Agency Settings")}
        </Link>
      ),
      key: "1",
      icon: <Icon icon="AiOutlineSetting" />,
      disabled: !getPrivilege("can_update_agency_settings", userPrivileges),
      onClick: () => {},
    },
    {
      label: t("Scan Qr Code"),
      key: "2",
      icon: <Icon icon="AiOutlineScan" />,
      onClick: () => {
        qrCodeModalRef?.current?.setVisible(true)
      },
    },
    {
      label: t("Scan GA Qr Code"),
      key: "3",
      icon: <Icon icon="AiOutlineScan" />,
      onClick: () => {
        gAQrCodeModalRef?.current?.setVisible(true)
      },
      disabled: !getPrivilege("can_request_google_auth_code", userPrivileges),
    },
    {
      label: t("Logout"),
      key: "4",
      icon: isLoading ? <Spinner type="plain" className="p-1" /> : <Icon icon="FiLogOut" />,
      disabled: isLoading,
      onClick: handleLogout,
    },
  ]

  return (
    <>
      <div style={{ display: "flex", alignItems: "center" }}>
        <Dropdown
          menu={{ items }}
          dropdownRender={menu => (
            <div className={styles.userMenuOuter}>
              <div className={styles.userMenuHeader}>
                <div className={styles.userAvatar}>
                  <Avatar
                    size={54}
                    src={user?.transformedPicture ? user?.transformedPicture : null}
                    icon={!user?.transformedPicture && <Icon icon={"FiUser"} size={30} />}
                  />
                </div>
                <div className={styles.userInfo}>
                  <Title level={5}>{user?.name}</Title>
                  <Text>{user?.email}</Text>
                  <div className={styles.userDesignation}>
                    <Text>{user?.UserDesignation?.name}</Text>
                    <Text>{user?.City?.name}</Text>
                  </div>
                </div>
              </div>
              <Link to={EDIT_STAFF_PATH.replace(":staffId", user?.id)} onClick={() => onClickLink({ key: "staff" })}>
                <Button type="primary" className={styles.btn} shape="round" block>
                  {t("Manage Profile")}
                </Button>
              </Link>
              <Divider style={{ marginBlock: "1rem" }} />
              <div className="fs16">{menu}</div>
            </div>
          )}
          className="d-grid"
        >
          <div>
            <Button
              type="light"
              color={theme["primary-color"]}
              shape="circle"
              style={{ padding: 0, width: 36, height: 36 }}
            >
              {user?.transformedPicture ? (
                <Avatar size={34} src={user?.transformedPicture} />
              ) : (
                <Icon size={20} icon="FiUser" />
              )}
            </Button>
          </div>
        </Dropdown>
      </div>
      <ScanQrCode
        ref={qrCodeModalRef}
        useQuery={useLazyGetQrCodeLoginQuery}
        title={"QR Code Login"}
        description={"Please scan the QR code with PropGo or Propforce App to login"}
        isTimeBound
        reFetchApiWhenExpires
      />
      <ScanQrCode
        ref={gAQrCodeModalRef}
        useQuery={useLazyGetGAQrCodeLoginQuery}
        title={"Scan Google Authenticator Code"}
        description={"Open the Google Authenticator app on your mobile device and scan the code."}
        requiresDecryption
      />
    </>
  )
}

export default UserMenu
